import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

function Topbar({ onMenuClick }) {
  const { user, signOut: contextSignOut } = useAuth();
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // Map routes to page titles
  const getPageTitle = () => {
    const pathname = location.pathname;
    if (pathname === '/' || pathname === '') {
      return 'Dashboard';
    } else if (pathname === '/profile' || pathname.startsWith('/profile')) {
      return 'User Profile';
    }
    // Default fallback
    return 'Dashboard';
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    contextSignOut();
  };

  const userEmail = user?.email || user?.email_address || 'User';
  const userName = user?.name || user?.nickname || userEmail.split('@')[0];
  const userNickname = user?.nickname;

  return (
    <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-gray-200">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-gray-900">
            {getPageTitle()}
          </h1>
        </div>

        <div className="flex items-center gap-4">


          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className='w-8 h-8' id="Layer_1" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" data-name="Layer 1"><ellipse cx="255.999" cy="116.444" fill="#00ffb0" rx="94.445" ry="94.44" transform="matrix(.003 -1 1 .003 138.899 372.144)"/><path d="m189.327 270.173h133.344c75.716 0 137.316 61.6 137.316 137.33v63.379a17.135 17.135 0 0 1 -17.107 17.118h-59.25v-93.518a12 12 0 1 0 -24 0v93.518h-207.262v-93.518a12 12 0 1 0 -24 0v93.518h-59.25a17.142 17.142 0 0 1 -17.111-17.121v-63.379c0-75.73 61.6-137.33 137.32-137.33zm-161.32 137.327v63.379a41.165 41.165 0 0 0 41.111 41.121h373.762a41.165 41.165 0 0 0 41.111-41.12v-63.38a161.745 161.745 0 0 0 -161.32-161.329h-133.344a161.762 161.762 0 0 0 -161.32 161.329zm135.552-291.056a92.44 92.44 0 1 1 92.441 92.445 92.55 92.55 0 0 1 -92.44-92.445zm208.88 0a116.44 116.44 0 1 0 -116.439 116.444 116.574 116.574 0 0 0 116.439-116.444z" fill="#143565" fillRule="evenodd"/></svg>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-gray-900">{userNickname || userName}</div>
                <div className="text-xs text-gray-500">{userEmail}</div>
              </div>
              <svg
                className={`h-4 w-4 text-gray-400 transition-transform ${isProfileOpen ? 'transform rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <AnimatePresence>
              {isProfileOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                  className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                >
                  <div className="py-1">
                    <button
                      onClick={handleSignOut}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Topbar;